import { StateCreator } from 'zustand'
import { logger, enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { SharedSlice } from '../../../../../slice'
import { PayRunsSlice } from '../slice'
import { PayrollSystem } from '$constants'
import { PayRun } from '$models'
import { payRunsService } from '$services'

interface EditPayRunState {
  payRun: PayRun | null
}

export interface EditPayRunSlice extends EditPayRunState {
  /**
   * Reset PayRun Slice
   * @returns a void
   */
  resetEditPayRunSlice: () => void

  /**
   * Get PayRun By id
   * @param id PayRun id
   * @param payrollSystem Payroll System that the PayRun belongs to
   * @returns a Promise<void>
   */
  getPayRun: (id: string, payrollSystem: PayrollSystem) => Promise<void>

  /**
   * Update PayRun
   * @param payRun PayRun to update
   * @param payrollSystem Payroll System that the PayRun belongs to
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns a Promise<void>
   */
  updatePayRun: (
    payRun: PayRun,
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Sync employee/s wages
   * @param payRunId is the payRun id
   * @param employeeIds are the ids of the employees that needs to be synced
   * @param payrollSystem Payroll System that the PayRun belongs to
   * @param successMessage Success Message to display if the sync was successful
   * @param errorMessage Error Message to display if the sync has failed
   * @param signal AbortSignal to cancel the request
   * @returns a Promise<void>
   */
  syncWages: (
    payRunId: string,
    employeeIds: string[],
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string,
    signal?: AbortSignal
  ) => Promise<boolean>
}

const initialState: EditPayRunState = {
  payRun: null, //TODO: remove null
}

const createEditPayRunSlice: StateCreator<
  PayRunsSlice & EditPayRunSlice & SharedSlice,
  [],
  [],
  EditPayRunSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetEditPayRunSlice: () => set(initialState),

  // State API calls
  getPayRun: async (
    id: string,
    payrollSystem: PayrollSystem
  ): Promise<void> => {
    return payRunsService
      .getPayRunById(id, payrollSystem)
      .then((payRun) => set(() => ({ payRun })))
      .catch((error) => {
        set(() => ({
          payRun: null,
        }))
        exceptionMessageHandler(error, 'Failed to retrieve pay run')
      })
  },

  updatePayRun: async (
    payRun: PayRun,
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      const updatedPayRun = await payRunsService.updatePayRun(
        payRun,
        payrollSystem
      )
      if (!updatedPayRun) {
        logger.error(`${errorMessage} - updatedPayRun doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      set(() => ({
        payRun: updatedPayRun,
      }))
      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },

  syncWages: async (
    payRunId: string,
    employeeIds: string[],
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string,
    signal?: AbortSignal
  ): Promise<boolean> => {
    try {
      const updatedPayRun = await payRunsService.syncWages(
        payRunId,
        employeeIds,
        payrollSystem,
        signal
      )
      if (!updatedPayRun) {
        logger.error(`${errorMessage} - updatedPayRun doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return false
      }

      set(() => ({
        payRun: updatedPayRun,
      }))
      enqueueLoadedSnackbar(successMessage, 'success')
      return true
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return false
    }
  },
})

export default createEditPayRunSlice
